import { Folder } from '@/api/generatedApi';
import { ModalTestIds } from '@/enums/modalTestIds.enum';
import { useDeleteFolder } from '@/hooks/api/folders/useDeleteFolder';
import { useGetFolder } from '@/hooks/api/folders/useGetFolder';
import { useToast } from '@/hooks/useToast';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BodyText } from '../../BodyText';
import { TitleText } from '../../TitleText';
import { DeletionContentsTree } from './DeletionContentsTree';

const formatSummary = (folder: Folder): string => {
  const parts: string[] = [];
  if (folder.assetsCount > 0) {
    parts.push(`${folder.assetsCount} ${folder.assetsCount === 1 ? 'asset' : 'assets'}`);
  }
  if (folder.foldersCount > 0) {
    parts.push(`${folder.foldersCount} ${folder.foldersCount === 1 ? 'subfolder' : 'subfolders'}`);
  }
  if (parts.length > 0) {
    return `Are you sure you want to delete "${folder.name}", including ${parts.join(' and ')}? This action cannot be undone.`;
  }
  return `Are you sure you want to delete "${folder.name}"? This action cannot be undone.`;
};

export const DeleteFolderModal = ({
  isOpen,
  onClose,
  folder,
  onDelete,
}: {
  isOpen: boolean;
  onClose: () => void;
  folder?: Folder;
  onDelete: ({ parentFolder }: { parentFolder?: Folder }) => void;
}) => {
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [error, setError] = useState<string>();
  const { showToast } = useToast();

  const { folder: parentFolder, isLoading: isLoadingParent } = useGetFolder({
    folderId: folder?.parentFolderId,
  });

  const { mutate, isPending } = useDeleteFolder({
    folder,
    onSuccess: () => {
      showToast({ message: `Deleted "${folder?.name}"`, type: 'success' });
      onClose();

      if (folder) {
        const updatedParameters = new URLSearchParams(searchParameters);
        if (parentFolder?.id) {
          updatedParameters.set('folderId', parentFolder.id);
        } else {
          updatedParameters.delete('folderId');
        }
        setSearchParameters(updatedParameters);
        onDelete({ parentFolder });
      }
    },
    onError: setError,
  });

  if (!folder?.id) {
    return;
  }

  const hasContents = folder.assetsCount > 0 || folder.foldersCount > 0;

  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <TitleText sx={{ padding: '24px 8px 0 24px' }}>Delete Folder</TitleText>
      <DialogContent sx={{ padding: '8px 24px 24px 24px' }}>
        <BodyText color='text.primary'>{formatSummary(folder)}</BodyText>
        {hasContents && (
          <Box
            sx={{
              mt: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 250,
              overflowY: 'auto',
              p: 1,
            }}
          >
            <DeletionContentsTree folder={folder} />
          </Box>
        )}
        {error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        <Button
          onClick={onClose}
          disabled={isPending}
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '12px 16px' }}
        >
          <BodyText color='text.primary'>Cancel</BodyText>
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={() => mutate()}
          disabled={isPending || isLoadingParent}
          data-testid={ModalTestIds.DeleteModalConfirmButton}
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}
        >
          {isPending ? 'Deleting...' : 'Confirm Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
